import { useMemo } from 'react';
import type { OutreachCampaign } from '@vagusx/clients';
import { Badge } from '@/components/ui/badge';
import { CallCalendar, useFilterNames } from './editor';
import { summarizeCampaign, type CampaignSummary } from './setup-summary';

/**
 * What the campaign does, read-only: its published version laid out to scan,
 * and what a patient who never picks up would get. Editing is the editor's.
 */
export function SetupSummary({
  campaign,
  active,
}: {
  campaign: OutreachCampaign;
  /** The tab is showing; the calendar simulates only then. */
  active: boolean;
}): React.ReactElement {
  const { names } = useFilterNames();
  // The tab stays mounted while hidden, so it is not re-described on every render of the page.
  const summary = useMemo(
    () => (campaign.spec ? summarizeCampaign(campaign.spec, { names }) : null),
    [campaign.spec, names]
  );
  if (!campaign.spec || !summary) {
    return (
      <p className="text-sm text-muted-foreground">
        Nothing is published yet. Edit the campaign to set it up.
      </p>
    );
  }

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <div className="lg:col-span-2">
        <SummaryBody summary={summary} />
      </div>
      <CallCalendar
        spec={campaign.spec}
        fixedOutcome="no_answer"
        title="If they never pick up"
        variant="card"
        enabled={active}
      />
    </div>
  );
}

/** The left column. Split out so it renders from a summary alone, without the calendar. */
export function SummaryBody({ summary }: { summary: CampaignSummary }): React.ReactElement {
  const { facts, audience, sequence, ending } = summary;
  // "Anything else" needs a list before it: when no step carries on, every row says so itself.
  const anyCarriesOn =
    sequence.carriesOnOn !== null || sequence.rows.some((row) => !!row.carriesOnOn?.length);

  return (
    <div className="space-y-6">
      <dl className="grid gap-2 sm:grid-cols-3">
        <Fact label="Calling hours" value={facts.hours} />
        <Fact label="Attempts" value={facts.limit} />
        <Fact label="Time limit" value={facts.expiry} />
      </dl>

      <section className="space-y-2">
        <h3 className="text-sm font-medium">Who we call</h3>
        <div className="space-y-1">
          {audience.sentences.map((sentence, i) => (
            <p key={sentence.id} className={i === 0 ? 'text-sm' : 'text-sm text-muted-foreground'}>
              {sentence.text}
            </p>
          ))}
        </div>
        {audience.requires.length > 0 && (
          <Chips label="Called once it has" items={audience.requires} />
        )}
      </section>

      <section className="space-y-2">
        <h3 className="text-sm font-medium">Sequence</h3>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-muted-foreground">
              <th className="w-6 pb-2 font-normal">
                <span className="sr-only">Order</span>
              </th>
              <th className="pb-2 pr-3 font-normal">Step</th>
              <th className="pb-2 pr-3 font-normal">When</th>
              <th className="pb-2 font-normal">What it does</th>
            </tr>
          </thead>
          <tbody>
            {sequence.rows.map((row, index) => (
              <tr key={row.id} className="border-t align-top">
                <td className="py-2 text-muted-foreground">{index + 1}</td>
                <td className="py-2 pr-3">
                  <p>{row.name}</p>
                  {row.details.map((text, i) => (
                    <p key={i} className="text-xs text-muted-foreground">
                      {text}
                    </p>
                  ))}
                  {row.carriesOnOn &&
                    (row.carriesOnOn.length === 0 ? (
                      <p className="text-xs text-muted-foreground">Always ends the run here.</p>
                    ) : (
                      <Chips label="Moves on when" items={row.carriesOnOn} />
                    ))}
                </td>
                <td className="py-2 pr-3 text-muted-foreground">{capitalize(row.when)}</td>
                <td className="py-2">{capitalize(row.action)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {(sequence.everyStep.length > 0 || sequence.carriesOnOn) && (
          <div className="space-y-2 border-t pt-3">
            {sequence.everyStep.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Every step</p>
                {sequence.everyStep.map((text, i) => (
                  <p key={i} className="text-sm text-muted-foreground">
                    {text}
                  </p>
                ))}
              </div>
            )}
            {sequence.carriesOnOn && (
              <Chips label="Moves to the next step when" items={sequence.carriesOnOn} />
            )}
          </div>
        )}
        <p className="text-sm text-muted-foreground">
          {anyCarriesOn && 'Anything else ends the run. '}
          {sequence.lastStepName} is the last step: the run finishes however it ends.
        </p>
      </section>

      <section className="space-y-2">
        <h3 className="text-sm font-medium">When it ends</h3>
        <p className="text-sm text-muted-foreground">{ending.success}</p>
        {ending.stopsOn.length > 0 && (
          <Chips label="Stops at any step when" items={ending.stopsOn} />
        )}
      </section>
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }): React.ReactElement {
  return (
    <div className="rounded-md bg-muted/50 px-3 py-2">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium">{value}</dd>
    </div>
  );
}

function Chips({ label, items }: { label: string; items: readonly string[] }): React.ReactElement {
  return (
    <div className="space-y-1 pt-1">
      <p className="text-xs text-muted-foreground">{label}</p>
      <div className="flex flex-wrap gap-1">
        {items.map((item, i) => (
          <Badge key={i} variant="outline" className="font-normal">
            {capitalize(item)}
          </Badge>
        ))}
      </div>
    </div>
  );
}

/** The shared phrases are sentence-medial (`as soon as they qualify`); a cell or chip starts one. */
function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}
